import type { ConflictExistingMember, MemberConflictMatchedBy } from '@safetyhub/shared';
import { Site, sitesApi } from './organizations';
// 에러 해석은 **정의 모듈에서 직접** 가져온다 — organizations의 re-export를 타면 그쪽을 mock한
// 테스트가 code/details를 지워 버려, 409를 일반 실패로 오인하게 된다(실제로 그랬다).
import { extractApiError, getHttpStatus } from './api';
import { upsertWorkerStats, upsertAccidentStats } from './site-stats';
import { createSiteContact } from './tbm';
import { createSiteProcessType } from './site-metadata';
import type {
  SiteCreationConstruction,
  PendingProcessType,
  WorkerStatsByYear,
  AccidentStatsByYear,
} from '../hooks/useSiteCreationState';
import type { ContactFormValue } from '../components/sites/ContactFormFields';

export interface CommitCreationState {
  construction: SiteCreationConstruction;
  products: string;
  contacts: ContactFormValue[];
  workerStats: WorkerStatsByYear;
  accidentStats: AccidentStatsByYear;
  processTypes: PendingProcessType[];
}

export interface CommitDependencies {
  companyId: string;
  creationState: CommitCreationState;
}

/**
 * 담당자 저장 중 만난 **인원 중복**(409 MEMBER_PHONE_CONFLICT) 1건.
 *
 * 이건 실패가 아니라 "이 회사에 이미 있는 사람"이라는 신호다 — 같은 사람의 Member를 하나 더
 * 만들지 않기 위해 서버가 판단을 넘긴 것이다. 호출부가 MemberPhoneConflictModal로 물어보고
 * `memberId` 경로로 다시 저장하면 기존 인원에 연결된다. 실패 목록에 섞으면 사용자는 그냥
 * "저장 실패"로 읽고 해결할 방법을 못 찾는다(2026-08-11 실사고).
 */
export interface ContactConflict {
  existingMember: ConflictExistingMember;
  pendingInput: ContactFormValue;
  /** 무엇이 겹쳤는지 — 안내 문구와 "다른 사람" 선택지 노출을 가른다 */
  matchedBy: MemberConflictMatchedBy;
}

export interface CommitResult {
  /** 사용자가 손쓸 수 없는 실패 — 안내만 한다. */
  failures: string[];
  /** 사용자 선택으로 해결 가능한 인원 중복 — 순서대로 물어본다. */
  contactConflicts: ContactConflict[];
}

async function trySave<T>(
  label: string,
  fn: () => Promise<T>,
  failures: string[],
): Promise<T | null> {
  try {
    return await fn();
  } catch (err) {
    console.error(`${label} 저장 실패`, extractApiError(err));
    failures.push(label);
    return null;
  }
}

async function tryBatch<T>(
  label: string,
  items: T[],
  fn: (item: T) => Promise<unknown>,
  failures: string[],
): Promise<void> {
  if (items.length === 0) return;
  let failedCount = 0;
  for (const item of items) {
    try {
      await fn(item);
    } catch (err) {
      console.error(`${label} 저장 실패`, extractApiError(err));
      failedCount++;
    }
  }
  if (failedCount > 0) {
    failures.push(`${label}(${failedCount}/${items.length})`);
  }
}

/**
 * Site 생성 후 sub-entity들을 순차 저장. 각 단계는 독립적으로 실패 허용.
 * 담당자/공정은 개별 실패를 카운트하여 `(성공/전체)` 형태로 라벨에 포함.
 *
 * 담당자의 **인원 중복(409)** 만은 실패와 분리해 `contactConflicts`로 돌려준다 — 호출부가
 * 물어보고 기존 인원에 연결할 수 있는 건이라 안내 문구로 끝내면 안 된다.
 */
export async function commitSubEntities(
  site: Site,
  deps: CommitDependencies,
): Promise<CommitResult> {
  const { companyId, creationState } = deps;
  const failures: string[] = [];
  const contactConflicts: ContactConflict[] = [];

  // 건설 필드 + products는 같은 PATCH — 실패 시 해당되는 항목 모두 failures에 추가
  const cons = creationState.construction;
  const hasConstruction =
    cons.constructionCost.trim() !== '' ||
    cons.constructionStart !== '' ||
    cons.constructionEnd !== '' ||
    cons.managementNumber.trim() !== '' ||
    cons.licenseNumber.trim() !== '' ||
    cons.progressRate !== '' ||
    cons.clientName.trim() !== '' ||
    cons.clientContact.trim() !== '';
  const trimmedProducts = creationState.products.trim();
  const hasProducts = trimmedProducts.length > 0;

  if (hasConstruction || hasProducts) {
    try {
      await sitesApi.update(
        companyId,
        site.id,
        {
          ...(hasConstruction && {
            // 숫자로 보낸다 — 서버 `@IsNumber`라 문자열은 400이었고 이 PATCH가 통째로 실패했다
            // (리뷰 1103 F3 · web `CreateSiteDto.constructionCost` JSDoc)
            constructionCost: cons.constructionCost.trim()
              ? Number(cons.constructionCost)
              : undefined,
            constructionStart: cons.constructionStart || undefined,
            constructionEnd: cons.constructionEnd || undefined,
            managementNumber: cons.managementNumber.trim() || undefined,
            licenseNumber: cons.licenseNumber.trim() || undefined,
            progressRate:
              cons.progressRate !== '' ? Number(cons.progressRate) : undefined,
            clientName: cons.clientName.trim() || undefined,
            clientContact: cons.clientContact.trim() || undefined,
          }),
          ...(hasProducts && { products: trimmedProducts }),
        },
        { silent: true },
      );
    } catch (err) {
      console.error('추가 정보 저장 실패', extractApiError(err));
      if (hasConstruction) failures.push('건설 정보');
      if (hasProducts) failures.push('생산품');
    }
  }

  // 담당자: 각 항목 독립 저장. 409(인원 중복)는 실패가 아니라 해결 대상으로 분리한다.
  let contactFailed = 0;
  for (const contact of creationState.contacts) {
    try {
      await createSiteContact(companyId, site.id, {
        name: contact.name,
        phone: contact.phone,
        department: contact.department || undefined,
        position: contact.position || undefined,
        email: contact.email || undefined,
      });
    } catch (err) {
      const conflict = extractContactConflict(err);
      if (conflict) {
        contactConflicts.push({
          existingMember: conflict.existingMember,
          matchedBy: conflict.matchedBy,
          pendingInput: contact,
        });
        continue;
      }
      console.error('담당자 저장 실패', extractApiError(err));
      contactFailed++;
    }
  }
  if (contactFailed > 0) {
    failures.push(`담당자(${contactFailed}/${creationState.contacts.length})`);
  }

  // 근로자 현황 (당해/직전 각각 upsert, 총합 0이면 스킵)
  for (const entry of [
    creationState.workerStats.current,
    creationState.workerStats.previous,
  ]) {
    if (!entry) continue;
    const total = entry.categories.reduce((sum, c) => sum + c.count, 0);
    if (total <= 0) continue;
    await trySave(
      '근로자 현황',
      () => upsertWorkerStats(companyId, site.id, entry),
      failures,
    );
  }

  // 재해발생 현황 (당해/직전 각각 upsert, 총합 0이면 스킵)
  for (const entry of [
    creationState.accidentStats.current,
    creationState.accidentStats.previous,
  ]) {
    if (!entry) continue;
    const total = entry.deathCount + entry.injuryCount + entry.diseaseCount;
    if (total <= 0) continue;
    await trySave(
      '재해발생 현황',
      () => upsertAccidentStats(companyId, site.id, entry),
      failures,
    );
  }

  // 공정 (UI 순서대로 순차 호출하여 서버 order 자동 부여)
  // 단일 실패 시 break하지 않고 계속 진행 - 나머지 공정 저장 시도
  await tryBatch(
    '공정',
    creationState.processTypes,
    (pt) => createSiteProcessType(companyId, site.id, { name: pt.name }),
    failures,
  );

  return { failures: Array.from(new Set(failures)), contactConflicts };
}

/**
 * 409 MEMBER_PHONE_CONFLICT에서 기존 인원 정보를 꺼낸다(아니면 null).
 * 판정 규약은 useSiteContactCreator(현장 상세 경로)와 **같아야 한다** — 두 경로가 다른 조건으로
 * 충돌을 인식하면 한쪽에서만 해결 모달이 뜬다.
 */
function extractContactConflict(
  err: unknown,
): { existingMember: ConflictExistingMember; matchedBy: MemberConflictMatchedBy } | null {
  if (getHttpStatus(err) !== 409) return null;
  const apiError = extractApiError(err);
  if (apiError.code !== 'MEMBER_PHONE_CONFLICT') return null;
  if (!apiError.details || !('existingMember' in apiError.details)) return null;
  const details = apiError.details as {
    existingMember: ConflictExistingMember;
    matchedBy?: MemberConflictMatchedBy;
  };
  // 구 서버 응답에는 matchedBy가 없다 — 그때는 기존 안내(전화번호)를 유지한다.
  return { existingMember: details.existingMember, matchedBy: details.matchedBy ?? 'phone' };
}
